import { createRng, hashSeed } from '../rng.js';

/**
 * Sucky Sucky, as a simulation.
 *
 * The cord is the entire game. Pushing a vacuum over a floor is not a skill;
 * pushing one with twenty-odd feet of cable that catches on every table leg you
 * walk past is a route problem, and a route problem is the only honest reason
 * this belongs in a class rather than in a cutscene.
 *
 * The cord is a real dragging one rather than a radius. It is a chain of pivot
 * points from the socket to the machine: go round a leg and that leg becomes a
 * pivot, so the cable now runs socket -> leg -> you and your reach is what is
 * left after the detour; come back the way you went and the pivot pops off
 * again. Wrap three legs and you are on a short leash in the corner of the
 * room, which is exactly what happens with a real one, and unwinding is a thing
 * you do by retracing rather than by pressing a button.
 *
 * Grid coordinates throughout, one cell to a foot and a half, so the room is
 * about the size of a small meeting room and the cord lengths in the catalogue
 * are in cells rather than in anything imaginary.
 */

/** The room, in cells. */
export const ROOM = Object.freeze({ cols: 18, rows: 13 });

/** Where the cable comes out of the wall. The bottom-left corner of the room. */
export const OUTLET = Object.freeze({ x: 0.5, y: 0.5 });

/** How fast the machine moves toward the hand, in cells a second. */
const SPEED = 5.4;

/** How close the head has to pass to a piece of trash to take it. */
const SUCK = 0.8;

/** A leg is a post the cable catches on, this far across. */
const LEG = 0.22;

/**
 * How long you have to pull on a taut cable before it jumps off a leg.
 *
 * The safety valve, and it is not generosity. Wraps can put the machine
 * somewhere that the only way to unwind is through a spot the taut cable will
 * not let it reach, which is a soft lock: the room is unfinishable and nothing
 * on screen says why. A real cord under tension slips off a smooth leg, so
 * this one does too, after six tenths of a second of straining.
 *
 * It costs enough to matter. Brute-forcing every wrap is most of a second each
 * and the clock is the thing the level is scored against, so routing properly
 * is still the way to pass; what this buys is that a badly wrapped room is a
 * slow room rather than a dead one.
 */
const SLIP_AFTER = 0.6;

/**
 * The lattice the tables sit on, and why every number in it is what it is.
 *
 * A loose grid with a jitter rather than a scatter, because a scatter puts two
 * tables touching about a third of the time. That matters more than it sounds:
 * the seventh table at level five can close the last corridor, and a room you
 * cannot walk across is not a harder room, it is a broken one. The first cut of
 * this had half-widths of 1.15 and a jitter of 0.45 on a 3.9 pitch, which left
 * a nominal gap of 0.6 and a real one that went negative — level five sealed
 * itself off and could not be passed by anybody.
 *
 * So the arithmetic is written down. A gap has to fit the machine, which needs
 * `half + CLEAR` either side of it, and the worst case is two tables leaning
 * toward each other by the jitter:
 *
 *   across - 2*half - 2*CLEAR - 2*jitter  =  4.2 - 1.8 - 1 - 0.72  =  0.68
 *
 * Two thirds of a foot of corridor at the worst roll on both axes, which is
 * the property that matters, and the far corner of the room stays inside the
 * shortest cord in the catalogue.
 */
const TABLE = Object.freeze({ x: 2.8, y: 2.4, acrossX: 4.2, acrossY: 4.2, half: 0.9, jitter: 0.18 });

const cross = (ax, ay, bx, by) => ax * by - ay * bx;

function sideOf(from, pivot, to) {
  return Math.sign(cross(pivot.x - from.x, pivot.y - from.y, to.x - from.x, to.y - from.y));
}

/** Does the triangle A-B-C contain P? Used to spot a leg being swept over. */
function swept(a, b, c, p) {
  const d1 = cross(b.x - a.x, b.y - a.y, p.x - a.x, p.y - a.y);
  const d2 = cross(c.x - b.x, c.y - b.y, p.x - b.x, p.y - b.y);
  const d3 = cross(a.x - c.x, a.y - c.y, p.x - c.x, p.y - c.y);
  const neg = d1 < 0 || d2 < 0 || d3 < 0;
  const pos = d1 > 0 || d2 > 0 || d3 > 0;
  return !(neg && pos);
}

export function createVacuum({ row, seed = 'vacuum' }) {
  const rng = createRng(hashSeed(seed, row.level));

  /*
   * Tables on a loose lattice rather than at random points, because a random
   * scatter puts two of them touching about a third of the time and a room you
   * cannot walk through is not a harder room, it is a broken one. Jittered
   * inside the cell so it is not a showroom either.
   */
  const tables = [];
  const slots = rng.shuffle(
    [0, 1, 2, 3].flatMap((col) => [0, 1, 2].map((r) => ({ col, row: r })))
  );
  for (const slot of slots.slice(0, row.tables)) {
    const x = TABLE.x + slot.col * TABLE.acrossX + (rng.next() - 0.5) * TABLE.jitter * 2;
    const y = TABLE.y + slot.row * TABLE.acrossY + (rng.next() - 0.5) * TABLE.jitter * 2;
    tables.push({ x, y, half: TABLE.half });
  }

  /** The four feet of each table. These are what the cable catches on. */
  const legs = tables.flatMap((table, index) =>
    [
      [-1, -1],
      [1, -1],
      [1, 1],
      [-1, 1],
    ].map(([sx, sy], corner) => ({
      id: `t${index}l${corner}`,
      x: table.x + sx * table.half,
      y: table.y + sy * table.half,
    }))
  );

  /** How close the machine's body gets to a table before it is touching it. */
  const CLEAR = 0.5;

  const clearOfTables = (x, y) =>
    tables.every(
      (table) => Math.abs(x - table.x) > table.half + CLEAR || Math.abs(y - table.y) > table.half + CLEAR
    );

  /**
   * Slides a point out of any table it has ended up inside.
   *
   * Out along the shallower axis, which is what makes it a slide rather than a
   * wall: driven at a table edge-on the machine runs along the edge and keeps
   * whatever movement it had in the other direction, and only a head driven
   * square into a face stops. A hard stop instead of this wedges the machine in
   * a corner with no way out but a route the player has to guess, and the cord
   * is supposed to be the difficult part of this class.
   */
  function slideOut(p) {
    let { x, y } = p;
    for (let pass = 0; pass < 3; pass += 1) {
      let moved = false;
      for (const table of tables) {
        const r = table.half + CLEAR;
        const dx = x - table.x;
        const dy = y - table.y;
        if (Math.abs(dx) >= r || Math.abs(dy) >= r) continue;
        if (r - Math.abs(dx) < r - Math.abs(dy)) x = table.x + (dx < 0 ? -r : r);
        else y = table.y + (dy < 0 ? -r : r);
        moved = true;
      }
      if (!moved) break;
    }
    return { x, y };
  }

  /** Trash, anywhere on the floor that is not under a table. */
  const trash = [];
  for (let guard = 0; trash.length < row.trash && guard < row.trash * 60; guard += 1) {
    const x = 0.8 + rng.next() * (ROOM.cols - 1.6);
    const y = 0.8 + rng.next() * (ROOM.rows - 1.6);
    if (!clearOfTables(x, y)) continue;
    trash.push({ id: `r${trash.length}`, x, y, gone: false });
  }

  let head = { x: OUTLET.x + 1.2, y: OUTLET.y + 1.2 };
  let wanted = { ...head };
  /** Socket first, then every leg the cable is currently wrapped on. */
  let pivots = [{ x: OUTLET.x, y: OUTLET.y, id: 'outlet', side: 0 }];
  let taken = 0;
  let left = row.seconds;
  /** Seconds spent pulling against a cable that will not pay out any more. */
  let straining = 0;

  function cordLength(to) {
    let total = 0;
    for (let i = 1; i < pivots.length; i += 1) {
      total += Math.hypot(pivots[i].x - pivots[i - 1].x, pivots[i].y - pivots[i - 1].y);
    }
    const last = pivots[pivots.length - 1];
    return total + Math.hypot(to.x - last.x, to.y - last.y);
  }

  /** Legs the cable has just been dragged across become pivots. */
  function wrap(from, to) {
    const anchor = pivots[pivots.length - 1];
    for (const leg of legs) {
      if (pivots.some((pivot) => pivot.id === leg.id)) continue;
      if (!swept(anchor, from, to, leg)) continue;
      if (Math.hypot(leg.x - to.x, leg.y - to.y) < LEG) continue;
      pivots = [...pivots, { ...leg, side: sideOf(anchor, leg, to) }];
      return;
    }
  }

  /**
   * And come off again when you retrace your steps past them.
   *
   * Two ways off, and the second one is not a nicety. A pivot pops when the
   * machine crosses back over the line it wrapped on, which is the textbook
   * rope test and handles retracing your steps. It also pops when the machine
   * is right on top of the leg, because a cable dragged up to the post it is
   * caught on has no wrap left in it — and without that rule a machine that
   * walked all the way home still had four wraps on, sat at the socket with a
   * foot of slack and no way to lose any of it. That is a room nobody can
   * finish, and the only sign of it is a vacuum that has stopped working.
   */
  function unwrap(to) {
    while (pivots.length > 1) {
      const last = pivots[pivots.length - 1];
      const before = pivots[pivots.length - 2];
      const onTopOfIt = Math.hypot(to.x - last.x, to.y - last.y) < 0.55;
      if (!onTopOfIt && sideOf(before, last, to) === last.side) break;
      pivots = pivots.slice(0, -1);
    }
  }

  return {
    get level() {
      return row.level;
    },
    get target() {
      return row.target;
    },
    /** Coverage: what fraction of the floor's trash is in the machine. */
    get score() {
      return trash.length ? taken / trash.length : 1;
    },
    get taken() {
      return taken;
    },
    get total() {
      return trash.length;
    },
    get secondsLeft() {
      return Math.max(0, left);
    },
    get head() {
      return { ...head };
    },
    get cord() {
      return [...pivots.map((pivot) => ({ x: pivot.x, y: pivot.y })), { ...head }];
    },
    get slack() {
      return Math.max(0, row.cord - cordLength(head));
    },
    get wraps() {
      return pivots.length - 1;
    },
    /** How long the cable has been taut with the machine still pulling. */
    get straining() {
      return straining;
    },
    get tables() {
      return tables.map((table) => ({ ...table }));
    },
    get legs() {
      return legs.map((leg) => ({ ...leg }));
    },
    get trash() {
      return trash.map((piece) => ({ ...piece }));
    },
    get done() {
      return left <= 0 || taken >= trash.length;
    },
    get passed() {
      return this.score >= row.target;
    },

    /** Where the hand is, in cells. The machine walks there, it does not jump. */
    moveTo(x, y) {
      wanted = {
        x: Math.max(0.4, Math.min(ROOM.cols - 0.4, x)),
        y: Math.max(0.4, Math.min(ROOM.rows - 0.4, y)),
      };
    },

    step(dt) {
      if (this.done) return;
      left -= dt;

      const dx = wanted.x - head.x;
      const dy = wanted.y - head.y;
      const distance = Math.hypot(dx, dy);
      if (distance > 1e-4) {
        const move = Math.min(distance, SPEED * dt);
        let to = { x: head.x + (dx / distance) * move, y: head.y + (dy / distance) * move };

        /*
         * The leash. If the step would pay out more cable than there is, the
         * machine goes as far as the cable allows and stops there rather than
         * being teleported back or silently refusing: a vacuum on a taut cord
         * still moves, it just stops moving away.
         */
        if (cordLength(to) > row.cord) {
          const anchor = pivots[pivots.length - 1];
          let low = 0;
          let high = 1;
          for (let i = 0; i < 18; i += 1) {
            const mid = (low + high) / 2;
            const at = { x: head.x + (to.x - head.x) * mid, y: head.y + (to.y - head.y) * mid };
            if (cordLength(at) > row.cord) high = mid;
            else low = mid;
          }
          to = { x: head.x + (to.x - head.x) * low, y: head.y + (to.y - head.y) * low };
          // Sliding along the leash rather than stopping dead is what makes the
          // taut cord feel like a cord instead of a wall, and it is also what
          // lets a player work the far corner by arcing round it.
          if (low < 0.02) {
            to = { ...head };
            straining += dt;
            if (straining >= SLIP_AFTER && pivots.length > 1) {
              pivots = pivots.slice(0, -1);
              straining = 0;
            }
          } else straining = 0;
        } else straining = 0;

        /*
         * Tables are solid, and the machine slides along one rather than
         * stopping dead against it. See `slideOut`.
         */
        const slid = slideOut(to);
        if (cordLength(slid) <= row.cord + 1e-6) {
          unwrap(slid);
          wrap(head, slid);
          head = slid;
        }
      }

      for (const piece of trash) {
        if (piece.gone) continue;
        if (Math.hypot(piece.x - head.x, piece.y - head.y) > SUCK) continue;
        piece.gone = true;
        taken += 1;
      }
    },
  };
}
